import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Button,
} from '@mui/material';
import { formatCountryForDisplay } from '../data/locationData';
import { formatTimezoneDisplay } from '../utils/timezoneUtils';
import { splitStoredName } from '../utils/personName';
import { formatCalendarDate } from '../utils/dateTimeUtils';
import studentsService from '../services/students';
import ParentConsentSummary from './ParentConsentSummary';
import { teacherModuleAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

function SectionTitle({ children }) {
  return (
    <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
      {children}
    </Typography>
  );
}

function DetailField({ label, value, xs = 6 }) {
  return (
    <Grid item xs={12} sm={xs}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
        {value ?? 'Not provided'}
      </Typography>
    </Grid>
  );
}

function formatTimeSlot(time) {
  if (!time) return '';
  return String(time).slice(0, 5);
}

function TimePreferencesReadOnly({ preferences = [] }) {
  if (!preferences.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No time preferences configured.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {DAYS_OF_WEEK.map((day) => {
        const slots = preferences.filter((p) => Number(p.day_of_week) === day.value);
        if (!slots.length) return null;
        return (
          <Box key={day.value}>
            <Typography variant="subtitle2" color="text.secondary">
              {day.label}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {slots.map((slot) => (
                <Chip
                  key={slot.id || `${day.value}-${slot.start_time}-${slot.end_time}`}
                  size="small"
                  label={`${formatTimeSlot(slot.start_time)} – ${formatTimeSlot(slot.end_time)}${
                    slot.is_preferred ? ' (preferred)' : ''
                  }`}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default function StudentReadOnlyDialog({ open, onClose, studentId, batchId = null, showFinancial }) {
  const navigate = useNavigate();
  const { hasRole, canViewStudentFinancials } = useAuth();
  const canViewHandover = hasRole(['teacher', 'teacher_supervisor', 'teacher_admin']);
  const showFinancialSection =
    typeof showFinancial === 'boolean' ? showFinancial : canViewStudentFinancials();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [handoverAvailable, setHandoverAvailable] = useState(false);

  useEffect(() => {
    if (!open || !studentId) {
      setStudent(null);
      setError('');
      setHandoverAvailable(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setHandoverAvailable(false);

    studentsService
      .getStudent(studentId, batchId ? { batchId } : {})
      .then((res) => {
        if (cancelled) return;
        setStudent(res?.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setStudent(null);
        setError(err.response?.data?.message || 'Failed to load student details');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    if (canViewHandover && batchId) {
      teacherModuleAPI
        .getTeachingHandoverAvailability(studentId, { batchId })
        .then((res) => {
          if (!cancelled) setHandoverAvailable(!!res.data?.data?.available);
        })
        .catch(() => {
          if (!cancelled) setHandoverAvailable(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [open, studentId, batchId, canViewHandover]);

  const titleName = student
    ? [student.first_name, student.last_name].filter(Boolean).join(' ') || 'Student'
    : 'Student';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Student Details — {titleName}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && error ? <Alert severity="error">{error}</Alert> : null}

        {!loading && !error && student ? (
          <Box>
            <SectionTitle>Personal Information</SectionTitle>
            <Grid container spacing={2}>
              {student.display_id ? <DetailField label="Student ID" value={student.display_id} /> : null}
              <DetailField
                label="Full Name"
                value={[student.first_name, student.last_name].filter(Boolean).join(' ')}
              />
              <DetailField label="Email" value={student.email} />
              <DetailField label="Phone" value={student.phone} />
              <DetailField
                label="Date of Birth"
                value={
                  student.date_of_birth
                    ? formatCalendarDate(student.date_of_birth)
                    : null
                }
              />
              <DetailField label="Age" value={student.age != null ? `${student.age} years` : null} />
              <DetailField label="Class Grade" value={student.class_grade} />
            </Grid>

            <SectionTitle>Academic Information</SectionTitle>
            <Grid container spacing={2}>
              <DetailField label="Age Group" value={student.age_group} />
              <DetailField label="Student Type" value={student.student_type} />
              <DetailField label="Class Preference" value={student.class_type_preference} />
              <DetailField label="Learning Level" value={student.learning_level} />
              <DetailField label="Support Requirements" value={student.support_requirements || 'None specified'} xs={12} />
            </Grid>

            <SectionTitle>School Information</SectionTitle>
            <Grid container spacing={2}>
              <DetailField label="School Name" value={student.school_name} />
              <DetailField label="School Location" value={student.school_location} />
              <DetailField label="School Address" value={student.school_address} xs={12} />
            </Grid>

            <SectionTitle>Parent Information</SectionTitle>
            <Grid container spacing={2}>
              <DetailField label="Father's First Name" value={splitStoredName(student.father_name).first} />
              <DetailField label="Father's Last Name" value={splitStoredName(student.father_name).last} />
              <DetailField label="Mother's First Name" value={splitStoredName(student.mother_name).first} />
              <DetailField label="Mother's Last Name" value={splitStoredName(student.mother_name).last} />
            </Grid>

            <ParentConsentSummary consent={student.parent_consent} />

            <SectionTitle>Location</SectionTitle>
            <Grid container spacing={2}>
              <DetailField label="Country" value={formatCountryForDisplay(student.country)} />
              <DetailField label="State" value={student.state} />
              <DetailField label="City" value={student.city} />
              <DetailField label="Zip code" value={student.postal_code} />
              <DetailField label="Timezone" value={formatTimezoneDisplay(student.timezone || 'UTC')} />
              <DetailField label="Address" value={student.address} xs={12} />
            </Grid>

            <SectionTitle>Emergency Contact</SectionTitle>
            <Grid container spacing={2}>
              <DetailField label="Contact Name" value={student.emergency_contact_name} />
              <DetailField label="Contact Phone" value={student.emergency_contact_phone} />
            </Grid>

            {showFinancialSection ? (
              <>
                <SectionTitle>Financial Information</SectionTitle>
                <Grid container spacing={2}>
                  <DetailField label="Fees" value={student.fees} />
                  <DetailField label="Discount" value={student.discount || 'None'} />
                </Grid>
              </>
            ) : null}

            <SectionTitle>Additional Information</SectionTitle>
            <Grid container spacing={2}>
              <DetailField
                label="Sales Person"
                value={student.sales_person_name || 'Unassigned'}
              />
              <DetailField label="RM" value={student.rm_name || 'Unassigned'} />
              {batchId ? (
                <DetailField
                  label="First class in this batch"
                  value={
                    student.first_class_in_batch
                      ? formatCalendarDate(student.first_class_in_batch)
                      : 'Not yet'
                  }
                />
              ) : null}
              <DetailField
                label={batchId ? 'First class (any batch)' : 'First class'}
                value={
                  student.first_class_date
                    ? formatCalendarDate(student.first_class_date)
                    : 'Not yet'
                }
              />
              <DetailField label="Hobby" value={student.hobby} />
            </Grid>

            <SectionTitle>Batch Assignment</SectionTitle>
            {student.current_assignments?.length ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {student.current_assignments.map((a) => (
                  <Box key={a.id}>
                    <Typography variant="body1">{a.batch_name || a.batch_id}</Typography>
                    <Typography variant="body1">
                      Teacher: {a.teacher_name || 'Not assigned'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No active batch assignments.
              </Typography>
            )}

            <SectionTitle>Time Preferences</SectionTitle>
            <TimePreferencesReadOnly preferences={student.time_preferences} />

            {student.notes ? (
              <>
                <SectionTitle>Notes</SectionTitle>
                <Typography variant="body1" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
                  {student.notes}
                </Typography>
              </>
            ) : null}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        {canViewHandover ? (
          <Button
            onClick={() => {
              const qs = batchId ? `?batchId=${encodeURIComponent(batchId)}` : '';
              onClose();
              navigate(`/teacher/students/${studentId}/teaching-handover${qs}`);
            }}
            variant={handoverAvailable ? 'contained' : 'outlined'}
            sx={{ textTransform: 'none', mr: 'auto' }}
          >
            {handoverAvailable ? 'View teaching handover' : 'Teaching handover'}
          </Button>
        ) : null}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
